import { useMemo, useCallback } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { useAdminOverride } from "./useAdminOverride";
import {
  ROLE_ASSIGNMENTS,
  CATEGORY_ACCESS,
  APP_ACCESS,
} from "../config/access";

interface AppLike {
  id: string;
  category: string;
}

export function useAppAccess() {
  const { user } = useAuthContext();
  const { overrideEmail } = useAdminOverride();

  // Use override email if set, otherwise use actual user email
  const effectiveEmail = overrideEmail || user?.email;

  const userRoles = useMemo(() => {
    if (!effectiveEmail) return [];

    return Object.entries(ROLE_ASSIGNMENTS)
      .filter(([, emails]) => emails.includes(effectiveEmail))
      .map(([role]) => role);
  }, [effectiveEmail]);

  const canAccess = useCallback(
    (app: AppLike): boolean => {
      // Per-app override takes precedence
      if (APP_ACCESS[app.id]) {
        return APP_ACCESS[app.id].some((r) => userRoles.includes(r));
      }

      // Category-level restriction
      if (CATEGORY_ACCESS[app.category]) {
        return CATEGORY_ACCESS[app.category].some((r) => userRoles.includes(r));
      }

      // Default: everyone can access
      return true;
    },
    [userRoles]
  );

  // Has admin or dev role (for general privileged access like boards/pods)
  const isAdmin = useMemo(
    () => userRoles.includes("admin") || userRoles.includes("dev"),
    [userRoles]
  );

  // Has dev role specifically
  const isDev = useMemo(() => userRoles.includes("dev"), [userRoles]);

  // Check if the REAL logged-in user (not override) is dev - for showing edit UI/impersonate
  const isRealUserDev = useMemo(() => {
    const realEmail = user?.email;
    if (!realEmail) return false;
    return ROLE_ASSIGNMENTS.dev?.includes(realEmail) ?? false;
  }, [user?.email]);

  return { userRoles, canAccess, isAdmin, isDev, isRealUserDev };
}
